// Video Verification Modal Component
// Opens a full-screen modal with camera feed and instructions

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Video, Camera, StopCircle, Upload, CheckCircle, XCircle, 
  AlertTriangle, Loader2, RefreshCw, User, X,
  Mic, Eye, Play
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Recording steps - 6 steps, ~22 seconds total
const STEPS = [
  { id: 1, title: "Position Your Face", instruction: "Center your face in the frame", duration: 3 },
  { id: 2, title: "Say Your Name", instruction: "Say: 'I am [Your Name]'", duration: 4 },
  { id: 3, title: "Say Your Company", instruction: "Say: 'from [Your Company]'", duration: 4 },
  { id: 4, title: "Say the Platform", instruction: "Say: 'applying for GlobalVyapar'", duration: 4 },
  { id: 5, title: "Blink Naturally", instruction: "Blink your eyes 2-3 times", duration: 3 },
  { id: 6, title: "Turn Head", instruction: "Slowly turn head left, then right", duration: 4 },
];

const TOTAL_DURATION = STEPS.reduce((sum, step) => sum + step.duration, 0);

export default function VideoVerificationModal({ 
  isOpen, 
  onClose, 
  fullName, 
  companyName,
  userRole = 'seller',
  onVideoUploaded 
}) {
  const [phase, setPhase] = useState('idle'); // idle, camera, recording, preview, uploading, complete
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTimer, setStepTimer] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [cameraError, setCameraError] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhase('idle');
      setCurrentStep(0);
      setStepTimer(0);
      setTotalTime(0);
      setCameraError(null);
      setRecordedBlob(null);
      setRecordedUrl(null);
      setUploadProgress(0);
      setUploadResult(null);
    }
  }, [isOpen]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Effect to attach stream to video element when phase is 'camera' or 'recording' and video element is mounted
  useEffect(() => {
    if ((phase === 'camera' || phase === 'recording') && streamRef.current && videoRef.current) {
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;
        videoRef.current.play().catch(e => console.log('Autoplay handled:', e));
      }
    }
  }, [phase]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      
      streamRef.current = stream;
      setPhase('camera'); // This triggers re-render, useEffect will attach stream
    } catch (error) {
      console.error('Camera error:', error);
      setPhase('idle');
      if (error.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera access and try again.');
      } else if (error.name === 'NotFoundError') {
        setCameraError('No camera found. Please connect a camera.');
      } else {
        setCameraError(`Camera error: ${error.message}`);
      }
    }
  };

  const startRecording = () => {
    if (!streamRef.current) {
      toast.error('Camera not ready');
      return;
    }

    chunksRef.current = [];
    setCurrentStep(0);
    setStepTimer(0);
    setTotalTime(0);
    setPhase('recording');

    // Setup MediaRecorder
    const mimeTypes = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];
    let mimeType = mimeTypes.find(type => MediaRecorder.isTypeSupported(type)) || '';
    
    try {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current, mimeType ? { mimeType } : {});
    } catch (e) {
      mediaRecorderRef.current = new MediaRecorder(streamRef.current);
    }

    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mediaRecorderRef.current.mimeType || 'video/webm' });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      setPhase('preview');
      stopCamera();
    };

    mediaRecorderRef.current.start(1000);

    // Start step timer
    let stepTime = 0;
    let currentStepIndex = 0;
    let total = 0;

    timerRef.current = setInterval(() => {
      stepTime++;
      total++;
      setStepTimer(stepTime);
      setTotalTime(total);

      // Move to next step if current step duration completed
      if (stepTime >= STEPS[currentStepIndex].duration) {
        currentStepIndex++;
        stepTime = 0;
        setStepTimer(0);
        setCurrentStep(currentStepIndex);

        // If all steps done, stop recording
        if (currentStepIndex >= STEPS.length) {
          clearInterval(timerRef.current);
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          }
        }
      }
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const retake = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
    setCurrentStep(0);
    setStepTimer(0);
    setTotalTime(0);
    setPhase('idle');
  };

  const uploadVideo = async () => {
    if (!recordedBlob) {
      toast.error('No video to upload');
      return;
    }

    setPhase('uploading');
    setUploadProgress(0);

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        let base64 = reader.result;
        if (base64.includes(',')) base64 = base64.split(',')[1];

        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 5, 90));
        }, 300);

        const endpoint = userRole === 'buyer' 
          ? `${API}/api/buyer-verification/video/upload`
          : `${API}/api/seller-verification/video/upload`;

        const response = await axios.post(endpoint, {
          video_data: base64,
          expected_data: { full_name: fullName, company_name: companyName, verification_type: 'liveness' }
        }, {
          headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          timeout: 120000
        });

        clearInterval(progressInterval);
        setUploadProgress(100);
        setUploadResult(response.data);
        setPhase('complete');

        if (response.data.ai_recommendation === 'APPROVE') {
          toast.success('Video verified successfully!');
        } else {
          toast.info('Video submitted for admin review.');
        }

        if (onVideoUploaded) onVideoUploaded(response.data);
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(error.response?.data?.detail || 'Upload failed. Please try again.');
        setPhase('preview');
      }
    };
    reader.readAsDataURL(recordedBlob);
  };

  const handleClose = () => {
    stopCamera();
    if (timerRef.current) clearInterval(timerRef.current);
    onClose();
  };

  if (!isOpen) return null;

  const currentStepData = STEPS[currentStep] || STEPS[0];
  const stepProgress = (stepTimer / currentStepData.duration) * 100;
  const overallProgress = (totalTime / TOTAL_DURATION) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="video-verification-modal">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h2 className="text-white text-lg font-semibold">Identity Verification</h2>
        <button onClick={handleClose} className="text-white/70 hover:text-white p-2">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-start sm:justify-center p-4 overflow-y-auto">
        
        {/* Idle State - Show instructions and start button */}
        {phase === 'idle' && (
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-blue-600 flex items-center justify-center">
              <Video className="h-10 w-10 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Video Verification</h3>
              <p className="text-white/70">Follow 6 simple steps to verify your identity</p>
            </div>
            
            {/* Steps Preview */}
            <div className="bg-white/10 rounded-xl p-4 text-left">
              <p className="text-white/60 text-sm mb-3">What you will do ({TOTAL_DURATION} seconds):</p>
              <div className="space-y-2">
                {STEPS.map((step, idx) => (
                  <div key={step.id} className="flex items-center gap-3 text-white/80 text-sm">
                    <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">{idx + 1}</span>
                    <span>{step.title}</span>
                    <span className="ml-auto text-white/50">{step.duration}s</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Declaration Preview */}
            <div className="bg-blue-600/20 border border-blue-500/30 rounded-xl p-4">
              <p className="text-white/60 text-sm mb-2">You will say:</p>
              <p className="text-white font-medium italic">
                &quot;I am {fullName || '[Your Name]'} from {companyName || '[Your Company]'}, applying for GlobalVyapar&quot;
              </p>
            </div>

            {cameraError && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-xl p-4 text-red-300">
                <AlertTriangle className="h-5 w-5 inline mr-2" />
                {cameraError}
              </div>
            )}

            <Button 
              onClick={startCamera} 
              className="w-full h-14 text-lg bg-blue-600 hover:bg-blue-700"
              data-testid="start-camera-modal-btn"
            >
              <Camera className="h-6 w-6 mr-2" />
              Start Camera
            </Button>
          </div>
        )}

        {/* Camera Ready State AND Recording State - ONE persistent video element */}
        {(phase === 'camera' || phase === 'recording') && (
          <div className="max-w-2xl w-full space-y-4">
            {/* Video Feed - stays mounted for both camera and recording phases */}
            <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
                data-testid="camera-video-feed"
              />
              
              {/* Camera Ready badge - shown only in camera phase */}
              {phase === 'camera' && (
                <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Camera Ready
                </div>
              )}
              
              {/* Recording badges - shown only in recording phase */}
              {phase === 'recording' && (
                <>
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full flex items-center gap-2">
                    <span className="w-3 h-3 bg-white rounded-full animate-pulse"></span>
                    REC {totalTime}s
                  </div>
                  <div className="absolute top-4 left-4 bg-black/60 text-white px-4 py-2 rounded-full">
                    Step {currentStep + 1} of {STEPS.length}
                  </div>
                </>
              )}
            </div>

            {/* Camera phase instructions */}
            {phase === 'camera' && (
              <>
                <div className="bg-white/10 rounded-xl p-4 text-center">
                  <p className="text-white/70 mb-2">Make sure your face is clearly visible and you are in a well-lit area</p>
                  <p className="text-white font-medium">Click &quot;Start Recording&quot; when ready</p>
                </div>

                <div className="flex gap-3">
                  <Button onClick={handleClose} variant="outline" className="flex-1 h-12 border-white/20 text-white hover:bg-white/10">
                    Cancel
                  </Button>
                  <Button onClick={startRecording} className="flex-1 h-12 bg-red-600 hover:bg-red-700" data-testid="start-recording-btn">
                    <Play className="h-5 w-5 mr-2" />
                    Start Recording
                  </Button>
                </div>
              </>
            )}

            {/* Recording phase instructions */}
            {phase === 'recording' && (
              <>
                <div className="bg-blue-600 rounded-xl p-6 text-center">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-bold">
                      {currentStep + 1}
                    </div>
                    <div className="text-left">
                      <p className="text-white font-bold text-xl">{currentStepData.title}</p>
                      <p className="text-white/80">{currentStepData.instruction}</p>
                    </div>
                  </div>
                  <Progress value={stepProgress} className="h-2 bg-white/30" />
                  <p className="text-white/70 text-sm mt-2">
                    {Math.ceil(currentStepData.duration - stepTimer)}s remaining for this step
                  </p>
                </div>

                <div className="bg-white/10 rounded-xl p-4">
                  <div className="flex justify-between text-white/70 text-sm mb-2">
                    <span>Overall Progress</span>
                    <span>{Math.round(overallProgress)}%</span>
                  </div>
                  <Progress value={overallProgress} className="h-2" />
                </div>

                <Button onClick={stopRecording} variant="outline" className="w-full h-12 border-white/20 text-white hover:bg-white/10">
                  <StopCircle className="h-5 w-5 mr-2" />
                  Stop Recording Early
                </Button>
              </>
            )}
          </div>
        )}

        {/* Preview State - Show recorded video */}
        {phase === 'preview' && recordedUrl && (
          <div className="max-w-2xl w-full space-y-4">
            <div className="relative bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <video
                ref={previewRef}
                src={recordedUrl}
                controls
                playsInline
                className="w-full h-full object-contain"
                data-testid="recorded-video-preview"
              />
            </div>

            <div className="bg-green-600/20 border border-green-500/30 rounded-xl p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
              <p className="text-white font-medium">Recording Complete!</p>
              <p className="text-white/70 text-sm">Review your video and submit when ready</p>
            </div>

            <div className="flex gap-3">
              <Button onClick={retake} variant="outline" className="flex-1 h-12 border-white/20 text-white hover:bg-white/10">
                <RefreshCw className="h-5 w-5 mr-2" />
                Retake
              </Button>
              <Button onClick={uploadVideo} className="flex-1 h-12 bg-green-600 hover:bg-green-700" data-testid="submit-video-btn">
                <Upload className="h-5 w-5 mr-2" />
                Submit Video
              </Button>
            </div>
          </div>
        )}

        {/* Uploading State */}
        {phase === 'uploading' && (
          <div className="max-w-lg w-full text-center space-y-6">
            <Loader2 className="h-16 w-16 text-blue-500 mx-auto animate-spin" />
            <div>
              <p className="text-white font-medium text-xl">Uploading & Analyzing...</p>
              <p className="text-white/70">Please wait while we process your video</p>
            </div>
            <Progress value={uploadProgress} className="h-3" />
            <p className="text-white/50">{uploadProgress}%</p>
          </div>
        )}

        {/* Complete State */}
        {phase === 'complete' && (
          <div className="max-w-lg w-full text-center space-y-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-green-600 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <div>
              <p className="text-white font-bold text-2xl">Verification Submitted!</p>
              <p className="text-white/70 mt-2">
                {uploadResult?.ai_recommendation === 'APPROVE' 
                  ? 'Your video has been verified. Pending final admin approval.'
                  : 'Your video has been submitted for admin review.'}
              </p>
            </div>
            {uploadResult?.ai_match_percentage && (
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-white/60 text-sm">AI Confidence</p>
                <p className="text-white text-2xl font-bold">{uploadResult.ai_match_percentage}%</p>
              </div>
            )}
            <Button onClick={handleClose} className="w-full h-12 bg-blue-600 hover:bg-blue-700">
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
